/**
 * Per-person conveniences the CRM chats page kept in browser storage. Same
 * origin, same keys, so what people set on the old page carries over.
 */

/** Custom display names the person gave fans (fan-rename.js), by fan id. */
export function readCustomNames(scope: string): Record<string, string> {
  try {
    const parsed = JSON.parse(localStorage.getItem(`crm_fan_custom_names_${scope}`) || '{}')
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as Record<string, string>) : {}
  } catch {
    return {}
  }
}

export interface ActiveChat {
  fanId: string
  fanName: string
  view: 'chat'
}

/** The chat the person last had open on this account (cache.js saveActiveChatSession). */
export function readActiveChat(scope: string): ActiveChat | null {
  try {
    const raw = JSON.parse(localStorage.getItem(`crm_chats_active_fan_${scope}`) || 'null') as Partial<ActiveChat> | null
    const fanId = String(raw?.fanId ?? '').trim()
    return fanId ? { fanId, fanName: String(raw?.fanName ?? ''), view: 'chat' } : null
  } catch {
    return null
  }
}

export function saveActiveChat(scope: string, fanId: string, fanName: string) {
  try {
    localStorage.setItem(`crm_chats_active_fan_${scope}`, JSON.stringify({ fanId, fanName: fanName.slice(0, 240), view: 'chat' }))
  } catch {
    /* storage unavailable */
  }
}

export function clearActiveChat(scope: string) {
  try {
    localStorage.removeItem(`crm_chats_active_fan_${scope}`)
  } catch {
    /* storage unavailable */
  }
}

export const LIST_WIDTH = { min: 280, max: 520, default: 368 } as const

export function clampListWidth(value: number): number {
  return Math.round(Math.max(LIST_WIDTH.min, Math.min(LIST_WIDTH.max, Number.isFinite(value) ? value : LIST_WIDTH.default)))
}

/** The chats list width the person dragged to (panel-resize.js crm_chat_list_width). */
export function readListWidth(): number {
  try {
    const raw = localStorage.getItem('crm_chat_list_width')
    return raw == null || raw === '' ? LIST_WIDTH.default : clampListWidth(parseInt(raw, 10))
  } catch {
    return LIST_WIDTH.default
  }
}

export function saveListWidth(px: number) {
  try {
    localStorage.setItem('crm_chat_list_width', String(clampListWidth(px)))
  } catch {
    /* storage unavailable */
  }
}
